import { defineStore } from "pinia";
import _ from "lodash";
import { AxiosResponse } from "axios";
import {
    AddEmployeeGroupRequest,
    CipPostResult,
    EnrollmentPii,
    EnrollmentState,
    EnrollmentVerifyRequest,
    FeeSchedule,
    FetchState,
    MfaStatusEnum,
    PartnerFeeWaiver,
    PrivateCipInfo,
    UserTermsType,
} from "@/shared/types";
import { FeeWaiverType } from "@/admin/types";
import * as UserService from "@/shared/api/userService";
import * as BillingService from "@/shared/api/billingService";
import { CognitoAuth as Auth } from "@/shared/api/auth";
import * as storeHelper from "@/shared/storeHelper";
import * as MigrationRedirect from "@/employee/enrollmentMigrationClaimRedirect";
import { CognitoUser } from "amazon-cognito-identity-js";
import { formatCurrency } from "@/shared/currency";
import { useNotifierStore } from "@/shared/store/notifier";
import { useUserStore } from "@/shared/store/user";
import { useMfaStore } from "@/shared/store/mfa";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useWizardStore } from "@/employee/store/wizard";

export const initialState: EnrollmentState = {
    fetchState: FetchState.Uninitialized,
    firstName: "",
    employerName: "",
    wasClaimedAlready: false,
    isClaimed: false,
    isEnrollmentDisabled: false,
    isSelfReportedEmployeeAndUnclaimedState: false,
    isEnrolled: false,
    claimLinkId: "",
    email: "",
    isClaiming: false,
    emailVerify: false,
    emailAvailability: null,
    feeScheduleState: {
        fetchState: FetchState.Uninitialized,
        feeSchedule: null
    },
    previouslyEnteredPii: null,
    feeWaiverFetchState: FetchState.Uninitialized,
    feeWaiver: null,
    displayLoginMessage: false,
    hasExistingAccount: false,
    hasAccountWithOtherEmployer: false,
};

const emptyFeeSchedule: FeeSchedule = {
    monthlyFeeAmount: 0,
    isEmployerBalanceFeeWaiverEnabled: false,
    investmentFees: []
};

export const useEnrollmentStore = defineStore("enrollment", {
    state: () => (_.cloneDeep(initialState)),
    getters: {
        isLoaded: state => state.fetchState === FetchState.Loaded,
        isEmailAvailabilityConfirmed: state => state.emailAvailability && state.emailAvailability.isAvailable,
        isEmailConfirming: state => state.emailAvailability && state.emailAvailability.isConfirmInProgress,
        feeScheduleFetchState: state => state.feeScheduleState.fetchState,
        feeScheduleLoaded: state => state.feeScheduleState.fetchState === FetchState.Loaded,
        feeSchedule: state => state.feeScheduleState.fetchState === FetchState.Loaded ?
            { ...state.feeScheduleState.feeSchedule } : { ...emptyFeeSchedule },
        feeWaiverThreshold: state => state.feeWaiver.feeWaiverThreshold,
        feeWaiverType: state => state.feeWaiver ? state.feeWaiver.feeWaiverType : FeeWaiverType.None,
        investmentFeeForAccountType: state => (t: string) =>
            state.feeScheduleState.feeSchedule.investmentFees.find(ift => ift.accountType === t),
        isSelfReportedEmployeeAndUnclaimed (): boolean {
            const featureFlagStore = useFeatureFlagStore();
            return this.isSelfReportedEmployeeAndUnclaimedState && featureFlagStore.flags.pauseOnboardingForUnclaimedSelfReportedEmployees;
        },
        showWaiver () {
            return this.feeWaiverType !== FeeWaiverType.None;
        },
        waiverText: state => {
            switch (state.feeWaiver.feeWaiverType) {
                case FeeWaiverType.None:
                    return "";
                case FeeWaiverType.Balance:
                    return `*The Monthly Administration Fee is waived if the average daily balance of the Cash Account for the given month is equal to or exceeds ${formatCurrency(
                        state.feeWaiver.feeWaiverThreshold)}. `;
                case FeeWaiverType.BalanceAndInvestment:
                    return `*The Monthly Administration Fee is waived if the average daily balance of the Cash and Investment Account for the given month is equal to or exceeds ${formatCurrency(
                        state.feeWaiver.feeWaiverThreshold)}. `;
                default:
                    return "";
            }
        },
        showLoginMessage: state => state.displayLoginMessage
    },
    actions: {
        setClaimLinkId (claimLinkId: string) {
            const originalClaimLink = this.claimLinkId;
            this.claimLinkId = claimLinkId;
            if (originalClaimLink !== claimLinkId) {
                this.fetchState = FetchState.Uninitialized;
                this.isClaiming = false;
                this.isEnrolled = false;
                this.isClaimed = false;
                this.isEnrollmentDisabled = false;
                this.wasClaimedAlready = false;
                this.claimLinkId = claimLinkId;
            }
        },
        setEnrollmentEmail (email: string) {
            this.email = email;
        },
        setEnrollmentState (newState: EnrollmentState) {
            this.firstName = newState.firstName;
            this.employerName = newState.employerName;
            this.isClaimed = newState.isClaimed;
            this.isEnrollmentDisabled = newState.isEnrollmentDisabled;
            this.wasClaimedAlready = newState.isClaimed;
            this.isEnrolled = newState.isEnrolled;
            this.email = newState.email;
        },
        setFetchState (newState: FetchState) {
            this.fetchState = newState;
        },
        setFeeScheduleFetchState (newState: FetchState) {
            this.feeScheduleState.fetchState = newState;
        },
        setFeeSchedule (feeSchedule: FeeSchedule) {
            this.feeScheduleState.fetchState = FetchState.Loaded;
            this.feeScheduleState.feeSchedule = feeSchedule;
        },
        setClaiming (newState: boolean) {
            this.isClaiming = newState;
        },
        setEmailVerify (newState: boolean) {
            this.emailVerify = newState;
        },
        setEmailAvailabilityChecking (email: string) {
            this.emailAvailability = { email, isAvailable: null, isConfirmInProgress: true };
        },
        setEmailAvailability (emailCheckResult: { email: string, isAvailable: boolean }) {
            if (this.emailAvailability && this.emailAvailability.email === emailCheckResult.email) {
                this.emailAvailability = {
                    email: emailCheckResult.email,
                    isAvailable: emailCheckResult.isAvailable,
                    isConfirmInProgress: false
                };
            }
        },
        setEnrollmentComplete () {
            this.isEnrolled = true;
            this.isClaiming = false;
        },
        setPreviouslyEnteredPii (enrollmentPii: EnrollmentPii) {
            this.previouslyEnteredPii = enrollmentPii;
        },
        clearPreviouslyEnteredPii () {
            this.previouslyEnteredPii = null;
        },
        setFeeWaiver (f: PartnerFeeWaiver) {
            this.feeWaiver = f;
        },
        setFeeWaiverFetchState (f: FetchState) {
            this.feeWaiverFetchState = f;
        },
        pauseEnrollmentToFindEmployer () {
            this.isSelfReportedEmployeeAndUnclaimedState = true;
        },
        resetExistingAccountFlags () {
            this.hasAccountWithOtherEmployer = false;
            this.hasExistingAccount = false;
        },
        checkEmailAvailability (email: string) {
            if (!email) {
                this.emailAvailability = null;
                return;
            }

            if (this.emailAvailability && this.emailAvailability.email === email) {
                return;
            }
            const userStore = useUserStore();
            if (userStore.loggedIn) {
                return;
            }

            this.setEmailAvailabilityChecking(email);
            UserService
                .getEmployeeEmailAvailabilityByClaim(this.claimLinkId, email)
                .then(axiosResponse => {
                    const result = <{ isAvailable: boolean }>axiosResponse.data;
                    this.setEmailAvailability({ email, isAvailable: result.isAvailable });
                })
                .catch(() => {
                    const notifierStore = useNotifierStore();
                    if (!this.isClaimed) {
                        notifierStore.setDanger("There was a problem verifying the email.");
                    }
                });
        },
        addEmployeeGroupToCognitoUser (request: AddEmployeeGroupRequest): Promise<void> {
            if (!request.verificationCode) {
                return Promise.resolve();
            }
            return UserService.claimEmployeeAddEmployeeGroupToUser(request).then(_ => {
                return;
            });
        },
        loadFees (): Promise<void> {
            if (this.feeScheduleState.fetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            this.setFeeScheduleFetchState(FetchState.Loading);
            return BillingService.getFeeSchedule()
                .then(feeSchedule => this.setFeeSchedule(feeSchedule.data))
                .catch(err => {
                    const notifierStore = useNotifierStore();
                    this.setFeeScheduleFetchState(FetchState.Uninitialized);
                    notifierStore.setDanger("Fee schedule could not be loaded. Please try again later.");
                });
        },
        enrollmentStatusFromClaim (claimLinkId: string): Promise<void | EnrollmentState> {
            this.setClaimLinkId(claimLinkId);
            if (this.fetchState !== FetchState.Uninitialized) {
                return;
            }
            const notifierStore = useNotifierStore();
            const userStore = useUserStore();
            return new Promise<void>((resolve, reject) => {
                UserService
                    .getEmployeeClaim(claimLinkId)
                    .then(axiosResponse => {
                        const stateData = <EnrollmentState>axiosResponse.data;
                        this.setEnrollmentState(stateData);
                        this.setFetchState(FetchState.Loaded);


                        if (stateData.isClaimed) {
                            notifierStore.setDanger("This account has already been claimed");
                        } else {
                            // this condition is to handle the case where a user is attempting to claim
                            // and account on a browser tab where another user is already logged in

                            if (userStore.loggedIn) {
                                userStore.logout();
                            }

                            this.checkEmailAvailability(stateData.email);
                        }

                        resolve();
                    })
                    .catch(err => {
                        this.setFetchState(FetchState.Uninitialized);
                        if (err.response.status === 404) {
                            notifierStore.setDanger(`This link is no longer valid.
                    Please contact your Employer to receive a new link.`);
                            return;
                        }
                        reject(err);
                    });
            }).then(() => {
                return userStore.disableRequiredGroups();
            });
        },
        claimUserAccountBegin (args: EnrollmentVerifyRequest): Promise<void> {
            if (this.isClaiming) {
                return;
            }
            this.setClaiming(true);
            this.setEmailVerify(false);
            this.setEnrollmentEmail(args.email);

            const userStore = useUserStore();
            const loggedInClaim = userStore.loggedIn;

            if (loggedInClaim) {
                return UserService.claimExistingUser(args.claimCode, args.last4Ssn, args.dateOfBirth, userStore.user.email)
                    .then(() => userStore.refreshUser())
                    .then(() => this.setEnrollmentComplete())
                    .catch(err => {
                        this.setClaiming(false);
                        throw err;
                    });
            }

            let emailMatch = null;
            let cognitoUsername;
            const mfaStore = useMfaStore();

            return UserService
                .claimEmployeeBegin(args.claimCode, args.last4Ssn, args.dateOfBirth, args.email)
                .then(result => {
                    const response = result.data;
                    emailMatch = response.isEmailMatch;
                    cognitoUsername = response.cognitoUsername;
                    return Auth().loginInitializePassword(response.cognitoUsername, response.temporaryPassword, args.password,
                        (s: MfaStatusEnum, u: CognitoUser) => {
                            mfaStore.setMfaStatus(s);
                            mfaStore.setCognitoUser(u);
                        });
                })
                .then(() => {
                    if (emailMatch) {
                        return UserService.claimEmployeeEnd(args.claimCode).then(() => null);
                    }
                    return Auth().verifyEmail();
                })
                .then(() => {
                    if (emailMatch) {
                        this.setEnrollmentComplete();
                    } else {
                        this.setEmailVerify(true);
                    }
                    return Promise.resolve();
                })
                .catch(err => {
                    this.setClaiming(false);
                    throw err;
                });
        },
        claimUserAccountEnd (verificationCode) {
            return Auth()
                .verifyEmail(verificationCode)
                .then(() => UserService.claimEmployeeEnd(this.claimLinkId))
                .then(() => this.setEnrollmentComplete());
        },
        saveAgreedToTerms: (termsVersion: string, termsType: UserTermsType): Promise<void> => {
            return UserService.postAgreedTerms(termsVersion, termsType).then(() => null);
        },
        saveAgreedToFeeTerms: (): Promise<AxiosResponse<void>> => {
            return BillingService.postAgreedToFeeTerms();
        },
        termsAccepted: (): Promise<any> => {
            return UserService.TermsAccepted().then(() => null);
        },
        assignCip (accountSetup: PrivateCipInfo) {
            const wizardStore = useWizardStore();
            return UserService.postIndividualSignupCip(accountSetup)
                .then(resp => {
                    if (resp.data.isEmployerMatch) {
                        // the user was automatically matched to a claim
                        this.clearPreviouslyEnteredPii();
                        wizardStore.setTransitionToEmployee(true);
                    } else if (resp.data.isSelfReportedEmployee) {
                        this.pauseEnrollmentToFindEmployer();
                    }
                })
                .catch(err => {
                    if (err.response && isDuplicateSsnError(err.response)) {
                        showClaimExistingAccountError();
                        return Promise.reject();

                        // the server returns an error for the migration account match
                        // but not the employer match, this is incidentally inconsistent, not meaningful
                    } else if (MigrationRedirect.shouldUserClaimMigratedAccount(err.response)) {

                        return MigrationRedirect.changeToClaimMigrationAccount(
                            err.response.data.migratedUserPartnerId
                        ).then(_ => Promise.reject());
                    } else if (err.response && err.response.data && err.response.data.hasExistingAccount === true) {
                        this.hasExistingAccount = true;
                        wizardStore.setPersonalDetailsStepIncomplete();
                         return Promise.reject();
                    } else if (err.response && err.response.data && err.response.data.hasAccountWithOtherEmployer === true) {
                        this.hasAccountWithOtherEmployer = true;
                        wizardStore.setPersonalDetailsStepIncomplete();
                         return Promise.reject();
                    } else {
                        return storeHelper.handleError({ error: err, rethrow: true });
                    }
                });
        },
        putUserEmployerName (employerName: string) {
            return UserService.putUserEmployerName(employerName)
                .catch(error => {
                    return storeHelper.handleError({ error, rethrow: true });
                });
        },
        loadFeeWaiverSettings (partnerId: number) {
            if (this.feeWaiverFetchState === FetchState.Loading) {
                return Promise.resolve();
            }

            this.setFeeWaiverFetchState(FetchState.Loading);
            return BillingService.getPartnerFeeWaiver(partnerId)
                .then(r => this.setFeeWaiver(r.data))
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }))
                .finally(() => this.setFeeWaiverFetchState(FetchState.Loaded));
        },
    }
});

const isDuplicateSsnError = (e: AxiosResponse<CipPostResult>) =>
    e && e.status === 400
    && e.data && e.data.isUserMatch === true;

const showClaimExistingAccountError = () => {
    const notifierStore = useNotifierStore();
    notifierStore.setDanger("The information you entered does not match our records or the account already exists.");
};